import { useEffect, useState } from "react";
import type { ExecutiveSummaryData } from "../../lib/intelligence/executive";
import type { MonthRoute } from "../../lib/intelligence/month-route";
import { fmtBRL, fmtNumber } from "../../lib/format";
import { IconArrowDown, IconArrowUp, IconMinus } from "../icons";

const MONTHS = ["janeiro", "fevereiro", "março", "abril", "maio", "junho", "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"];

/** "R$ 2,81 milhões" / "R$ 815 mil" — para a manchete e a rota. */
export function fmtCompactBRL(v: number): string {
  const a = Math.abs(v);
  const sign = v < 0 ? "-" : "";
  if (a >= 1_000_000) return `${sign}R$ ${(a / 1_000_000).toFixed(2).replace(".", ",")} ${a >= 2_000_000 ? "milhões" : "milhão"}`;
  if (a >= 1_000) return `${sign}R$ ${Math.round(a / 1_000)} mil`;
  return fmtBRL(v);
}

/** Data civil de SP. */
function spDay(t: Date) {
  const sp = new Date(t.getTime() - 3 * 3600_000);
  return { d: sp.getUTCDate(), m: sp.getUTCMonth() };
}

const STATUS: Record<ExecutiveSummaryData["status"], { dot: string; label: string }> = {
  critical: { dot: "bg-red-400", label: "Requer ação" },
  warning:  { dot: "bg-amber-300", label: "Pede atenção" },
  ok:       { dot: "bg-tm-300", label: "Sob controle" },
};

/**
 * Abertura da Visão Executiva: o período como uma viagem. A manchete diz quanto
 * a empresa faturou e como isso se compara; a rota mostra o faturamento
 * acumulado dia a dia, onde o período de comparação estava neste mesmo ponto e,
 * com o mês em andamento, onde ele fecha no ritmo atual.
 */
export function ExecutiveHero({
  route, summary, companyName, revenue, prevRevenue, comparisonLabel, loading,
}: {
  route: MonthRoute | null;
  summary: ExecutiveSummaryData;
  companyName: string;
  revenue: number | null;
  prevRevenue: number | null;
  comparisonLabel: string;
  loading?: boolean;
}) {
  // Um único movimento: o caminhão percorre a rota até hoje ao carregar.
  const [drawn, setDrawn] = useState(false);
  useEffect(() => {
    if (loading) return;
    const id = requestAnimationFrame(() => setDrawn(true));
    return () => cancelAnimationFrame(id);
  }, [loading]);

  const st = STATUS[summary.status];
  const revMove = summary.moves[0];

  let lead = "";
  if (route) {
    const a = spDay(route.start);
    const b = spDay(route.end);
    const today = spDay(new Date(route.start.getTime() + (Math.max(1, route.daysElapsed) - 1) * 86_400_000));
    if (route.daysTotal === 1) {
      lead = route.inProgress
        ? `Hoje, ${today.d} de ${MONTHS[today.m]}, a ${companyName} faturou`
        : `Em ${a.d} de ${MONTHS[a.m]}, a ${companyName} faturou`;
    } else if (route.inProgress) {
      lead = a.d === 1
        ? `Até ${today.d} de ${MONTHS[today.m]}, a ${companyName} faturou`
        : `De ${a.d} de ${MONTHS[a.m]} até hoje, ${today.d}, a ${companyName} faturou`;
    } else {
      lead = a.m === b.m && a.d === 1
        ? `Em ${MONTHS[a.m]}, a ${companyName} faturou`
        : `De ${a.d} de ${MONTHS[a.m]} a ${b.d} de ${MONTHS[b.m]}, a ${companyName} faturou`;
    }
  } else {
    lead = `No período, a ${companyName} faturou`;
  }

  // "setembro de 2026, mesmo período" → "do mesmo período de setembro de 2026"; "mês anterior" → "do mês anterior".
  const samePeriod = comparisonLabel.match(/^(.*?),? (?:— )?mesmo período$/);
  const compPhrase = samePeriod ? `do mesmo período de ${samePeriod[1]}` : `do ${comparisonLabel}`;
  // A variação vira um selo com seta (direção) e cor (verde = melhorou, vermelho = piorou).
  const dir = revMove?.change?.startsWith("-") ? "down" : revMove?.change?.startsWith("+") ? "up" : "flat";
  const comparison = revMove?.change && prevRevenue
    ? {
        pct: revMove.change.replace(/^[+-]/, ""),
        text: `${dir === "down" ? "abaixo" : dir === "up" ? "acima" : "igual"} ${compPhrase} (${fmtCompactBRL(prevRevenue)}).`,
      }
    : null;
  const ArrowIcon = dir === "up" ? IconArrowUp : dir === "down" ? IconArrowDown : IconMinus;
  const chipTone = revMove?.good == null
    ? "bg-white/15 text-white"
    : revMove.good ? "bg-tm-300 text-tm-900" : "bg-red-500 text-white";

  return (
    <section aria-labelledby="hero-executivo" className="rounded-2xl bg-tm-800 text-white overflow-hidden">
      <div className="px-6 pt-5 pb-1">
        <div className="flex flex-wrap items-start justify-between gap-3">
          {/* Manchete discreta: uma frase, com o valor em destaque moderado */}
          <div className="max-w-2xl">
            <h2 id="hero-executivo" className="text-base text-tm-100 leading-snug">
              {loading || revenue == null ? "Carregando o período…" : (
                <>
                  {lead}{" "}
                  <span className="font-display font-semibold text-2xl text-white tabular-nums align-baseline">{fmtCompactBRL(revenue)}</span>
                </>
              )}
            </h2>
            {!loading && comparison && (
              <p className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm text-tm-100 mt-1.5">
                <span className={`inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 font-semibold tabular-nums ${chipTone}`}>
                  <ArrowIcon className="w-3.5 h-3.5" />
                  {comparison.pct}
                </span>
                {comparison.text}
              </p>
            )}
          </div>

          {!loading && (
            <a
              href="#o-que-pede-acao"
              className="flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/15 px-4 py-2 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              <span className={`w-2.5 h-2.5 rounded-full ${st.dot}`} aria-hidden="true" />
              {st.label}
              <span className="text-tm-100 font-normal">{summary.headline.replace(/ no período\.$/, "")}</span>
            </a>
          )}
        </div>
      </div>

      {/* Períodos de 1–2 dias (hoje, ontem) não têm trajeto para desenhar: só a manchete. */}
      {route && route.daysTotal >= 3 && !loading && (
        <RouteChart route={route} prevRevenue={prevRevenue} comparisonLabel={comparisonLabel} drawn={drawn} />
      )}
      {/* Carregando também não tem gráfico: sem o espaçador, o texto colava embaixo. */}
      {(loading || !route || route.daysTotal < 3) && <div className="h-4" />}

    </section>
  );
}

const W = 1000;
const H = 130;

function RouteChart({
  route, prevRevenue, comparisonLabel, drawn,
}: { route: MonthRoute; prevRevenue: number | null; comparisonLabel: string; drawn: boolean }) {
  const { points, daysTotal, daysElapsed, total, projected } = route;
  const top = Math.max(total, projected ?? 0, prevRevenue ?? 0) * 1.1 || 1;
  const x = (day: number) => (day / daysTotal) * W;
  const y = (v: number) => H - (v / top) * (H - 12);

  const line = points.map((p) => `${x(p.day).toFixed(1)},${y(p.cumulative).toFixed(1)}`);
  const area = points.length ? `M0,${H} L0,${y(0)} L${line.join(" L")} L${x(daysElapsed)},${H} Z` : "";
  const path = points.length ? `M0,${y(0)} L${line.join(" L")}` : "";
  const todayX = x(daysElapsed);
  const todayPct = (todayX / W) * 100;
  const todayY = y(total);
  const prevY = prevRevenue != null ? y(prevRevenue) : null;
  // Com o caminhão longe da ponta, o valor projetado fica no fim da linha pontilhada.
  const projAtEnd = todayPct < 72;

  const startDay = spDay(route.start);
  const endDay = spDay(route.end);
  const todayDay = spDay(new Date(route.start.getTime() + (daysElapsed - 1) * 86_400_000));

  return (
    <figure className="px-6 pt-3 pb-4">
      <figcaption className="sr-only">
        Faturamento acumulado do dia 1 ao dia {daysElapsed} de {daysTotal}: {fmtBRL(total)}.
        {prevRevenue != null ? ` No ${comparisonLabel}, neste mesmo ponto: ${fmtBRL(prevRevenue)}.` : ""}
        {projected != null ? ` No ritmo atual, o período fecha em ${fmtBRL(projected)}.` : ""}
      </figcaption>

      <div className="relative" style={{ height: H }}>
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="absolute inset-0 w-full h-full overflow-visible" aria-hidden="true">
          <defs>
            <clipPath id="rota-revelada">
              <rect x="0" y="0" height={H} width={drawn ? W : 0} className="transition-[width] duration-1000 ease-out motion-reduce:transition-none" />
            </clipPath>
          </defs>
          {/* estrada: o período inteiro */}
          <line x1="0" y1={H} x2={W} y2={H} stroke="rgba(255,255,255,0.25)" strokeWidth="2" vectorEffect="non-scaling-stroke" />
          <g clipPath="url(#rota-revelada)">
            <path d={area} fill="rgb(var(--tm-400) / 0.28)" />
            <path d={path} fill="none" stroke="rgb(var(--tm-300))" strokeWidth="3" vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
          </g>
          {projected != null && (
            <line
              x1={todayX} y1={todayY} x2={W} y2={y(projected)}
              stroke="rgb(var(--tm-200))" strokeWidth="2" strokeDasharray="6 6" vectorEffect="non-scaling-stroke"
              className={`transition-opacity duration-500 delay-700 motion-reduce:transition-none ${drawn ? "opacity-100" : "opacity-0"}`}
            />
          )}
          {prevY != null && (
            <line x1={Math.max(0, todayX - 70)} y1={prevY} x2={todayX} y2={prevY} stroke="rgba(255,255,255,0.7)" strokeWidth="2" strokeDasharray="2 4" vectorEffect="non-scaling-stroke" />
          )}
        </svg>

        {/* caminhão no dia de hoje */}
        <div
          className="absolute -translate-x-1/2 -translate-y-full transition-[left] duration-1000 ease-out motion-reduce:transition-none"
          style={{ left: `${drawn ? todayPct : 0}%`, top: `${(todayY / H) * 100}%` }}
          aria-hidden="true"
        >
          <TruckGlyph />
        </div>

        {/* fim da linha pontilhada: onde o período fecha no ritmo atual (acima da ponta, numa linha só) */}
        {projected != null && projAtEnd && (
          <>
            <span
              className={`absolute w-2.5 h-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-tm-200 ring-2 ring-tm-800 transition-opacity duration-500 delay-700 motion-reduce:transition-none ${drawn ? "opacity-100" : "opacity-0"}`}
              style={{ left: "100%", top: `${(y(projected) / H) * 100}%` }}
              aria-hidden="true"
            />
            <p
              className={`absolute right-0 -translate-y-full whitespace-nowrap text-xs text-tm-100 transition-opacity duration-500 delay-700 motion-reduce:transition-none ${drawn ? "opacity-100" : "opacity-0"}`}
              style={{ top: `calc(${(y(projected) / H) * 100}% - 8px)` }}
              title={`Média diária faturada até hoje × ${fmtNumber(daysTotal)} dias. É uma estimativa, não uma meta.`}
            >
              No ritmo atual, fecha em{" "}
              <span className="font-display font-semibold text-sm text-white tabular-nums">{fmtCompactBRL(projected)}</span>
            </p>
          </>
        )}

        {/* onde o período de comparação estava neste ponto */}
        {prevY != null && prevRevenue != null && todayPct > 30 && (
          <p
            className="absolute text-xs text-white/80 whitespace-nowrap -translate-y-1/2"
            style={{ right: `${100 - todayPct + 8}%`, top: `${(prevY / H) * 100}%` }}
          >
            Em {comparisonLabel.replace(/,? (?:— )?mesmo período$/, "").replace(/ de \d{4}$/, "")}, neste ponto: {fmtCompactBRL(prevRevenue)}
          </p>
        )}

      </div>

      <div className="relative h-5 mt-2 text-xs text-tm-100 tabular-nums">
        <span className="absolute left-0">{startDay.d} {MONTHS[startDay.m]?.slice(0, 3)}</span>
        {route.inProgress && todayPct > 12 && todayPct < 88 && (
          <span className="absolute -translate-x-1/2 font-semibold text-white" style={{ left: `${todayPct}%` }}>
            hoje, {todayDay.d}
          </span>
        )}
        <span className="absolute right-0">{endDay.d} {MONTHS[endDay.m]?.slice(0, 3)}</span>
      </div>
      {/* Perto do fim do período o caminhão encosta na ponta da linha: aí a projeção vai para a legenda. */}
      {projected != null && !projAtEnd && (
        <p className="text-sm text-tm-100 mt-2">
          No ritmo atual, o período fecha em{" "}
          <span className="font-display font-semibold text-base text-white tabular-nums">{fmtCompactBRL(projected)}</span>
          <span className="text-xs"> (média diária faturada até hoje × {fmtNumber(daysTotal)} dias, uma estimativa e não uma meta)</span>
        </p>
      )}
    </figure>
  );
}

/** Caminhão de perfil, desenhado em traço — o marcador do "hoje" na rota. */
function TruckGlyph() {
  return (
    <svg width="44" height="26" viewBox="0 0 44 26" fill="none" className="drop-shadow">
      <rect x="1" y="3" width="26" height="15" rx="2" fill="white" />
      <path d="M27 8h8l6 6v4H27z" fill="rgb(var(--tm-200))" />
      <path d="M30 10h4.5l3.5 3.5H30z" fill="rgb(var(--tm-800))" />
      <circle cx="9" cy="20" r="3.5" fill="rgb(var(--tm-900))" stroke="white" strokeWidth="1.5" />
      <circle cx="34" cy="20" r="3.5" fill="rgb(var(--tm-900))" stroke="white" strokeWidth="1.5" />
    </svg>
  );
}
